import type { Placement } from "./types.ts";

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function normalizeRect(rect: Rect): Rect {
  const width = clamp(rect.width, 0.05, 1);
  const height = clamp(rect.height, 0.05, 1);
  return {
    x: clamp(rect.x, 0, 1 - width),
    y: clamp(rect.y, 0, 1 - height),
    width,
    height,
  };
}

export function rotatePoint(point: Point, center: Point, radians: number): Point {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}

export function placementCenter(p: Placement): Point {
  return { x: p.x + p.width / 2, y: p.y + p.height / 2 };
}

export function pointInPlacement(
  point: Point,
  placement: Placement,
  pageWidth: number,
  pageHeight: number,
): boolean {
  const center = placementCenter(placement);
  const local = rotatePoint(
    { x: point.x / pageWidth, y: point.y / pageHeight },
    center,
    -placement.rotation,
  );
  return (
    local.x >= placement.x &&
    local.x <= placement.x + placement.width &&
    local.y >= placement.y &&
    local.y <= placement.y + placement.height
  );
}

export function snapValue(value: number, grid = 0.02): number {
  return Math.round(value / grid) * grid;
}

export function sortByLayer(placements: Placement[]): Placement[] {
  return [...placements].sort((a, b) => a.layer - b.layer);
}

export function nextLayer(placements: Placement[]): number {
  if (placements.length === 0) return 0;
  return Math.max(...placements.map((p) => p.layer)) + 1;
}
